/**
 * PendingPaymentsWidget — shared by AdminDashboard, OwnerDashboard (summary
 * mode) and OfficeDashboard (Prompt 11E.8). Reads the same
 * `PaymentDueReport` shape both `/v1/dashboard/office` and the standalone
 * `/v1/reports/payment-due` report return — one source, no duplicate
 * aggregate path.
 *
 * `mode="full"` (Admin/Office): expandable row list with Record Payment
 * (pre-filled to that invoice) + Void (reason required) actions.
 * `mode="summary"` (Owner): count + ₹ total only, links to the chase-list
 * report.
 *
 * data-testid: candeza-dash-pending-payments
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Card, CardHeader, CardTitle, Badge, Button } from '@/components/common';
import { formatAmount } from '@/api/dashboard';
import type { PaymentDueReport, PaymentDueRow } from '@/api/dashboard';
import { useInvoice } from '@features/invoices/api/billing';
import { RecordPaymentModal, VoidInvoiceModal } from '@features/invoices/pages/InvoicesPage';

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child {
    border-bottom: none;
  }
`;

const RowInfo = styled.div``;

const RowName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const RowMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const RowActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  align-items: center;
`;

const RowAmount = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-right: ${({ theme }) => theme.spacing.md};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const SummaryCount = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyNote = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  margin: 0;
`;

interface RowActionsInvoiceModalsProps {
  invoiceId: string;
  action: 'pay' | 'void';
  onClose: () => void;
}

/** Fetches the full invoice on demand (the widget only has the row summary
 * shape) then opens the matching modal — invoked from the row's action
 * buttons below. */
const InvoiceActionModal: React.FC<RowActionsInvoiceModalsProps> = ({ invoiceId, action, onClose }) => {
  const { data: invoice, isLoading } = useInvoice(invoiceId);
  if (isLoading || !invoice) return null;
  return action === 'pay' ? (
    <RecordPaymentModal invoice={invoice} onClose={onClose} />
  ) : (
    <VoidInvoiceModal invoice={invoice} onClose={onClose} />
  );
};

export interface PendingPaymentsWidgetProps {
  report: PaymentDueReport | undefined;
  isLoading?: boolean;
  mode?: 'full' | 'summary';
}

export const PendingPaymentsWidget: React.FC<PendingPaymentsWidgetProps> = ({
  report,
  isLoading,
  mode = 'full',
}) => {
  const [activeAction, setActiveAction] = useState<{ invoiceId: string; action: 'pay' | 'void' } | null>(
    null,
  );

  if (mode === 'summary') {
    return (
      <Card padding="lg" data-testid="candeza-dash-pending-payments">
        <CardHeader>
          <CardTitle>Pending Payments</CardTitle>
        </CardHeader>
        {isLoading && <EmptyNote>Loading…</EmptyNote>}
        {!isLoading && report && (
          <SummaryRow>
            <div>
              <SummaryValue>{formatAmount(report.total_amount_due)}</SummaryValue>
              <SummaryCount>{report.total_count} invoice{report.total_count === 1 ? '' : 's'} outstanding</SummaryCount>
            </div>
            <Link to="/invoices" data-testid="candeza-dash-action-view-chase-list">
              <Button variant="secondary" size="sm">View chase list</Button>
            </Link>
          </SummaryRow>
        )}
      </Card>
    );
  }

  const rows: PaymentDueRow[] = report?.rows ?? [];

  return (
    <Card padding="lg" data-testid="candeza-dash-pending-payments">
      <CardHeader>
        <CardTitle>Pending Payments</CardTitle>
        {!isLoading && report && (
          <Badge variant={rows.length > 0 ? 'warning' : 'success'}>
            {formatAmount(report.total_amount_due)}
          </Badge>
        )}
      </CardHeader>
      {isLoading && <EmptyNote>Loading…</EmptyNote>}
      {!isLoading && rows.length === 0 && <EmptyNote>No pending payments.</EmptyNote>}
      {!isLoading &&
        rows.map((row) => (
          <Row key={row.invoice_id} data-testid={`candeza-dash-pending-payment-row-${row.invoice_id}`}>
            <RowInfo>
              <RowName>{row.student_name}</RowName>
              <RowMeta>
                {row.overdue_days > 0 ? `${row.overdue_days} days overdue` : `Due ${row.due_date}`}
              </RowMeta>
            </RowInfo>
            <RowActions>
              <RowAmount>{formatAmount(row.amount_due)}</RowAmount>
              <Button
                variant="primary"
                size="sm"
                data-testid={`candeza-dash-action-record-payment-${row.invoice_id}`}
                onClick={() => setActiveAction({ invoiceId: row.invoice_id, action: 'pay' })}
              >
                Record Payment
              </Button>
              <Button
                variant="ghost"
                size="sm"
                data-testid={`candeza-dash-action-void-${row.invoice_id}`}
                onClick={() => setActiveAction({ invoiceId: row.invoice_id, action: 'void' })}
              >
                Void
              </Button>
            </RowActions>
          </Row>
        ))}

      {activeAction && (
        <InvoiceActionModal
          invoiceId={activeAction.invoiceId}
          action={activeAction.action}
          onClose={() => setActiveAction(null)}
        />
      )}
    </Card>
  );
};
